/**
 * 소셜 로그인 버튼 셋 (D108 · 히어로챗 components/social.jsx · auth-sheet.css 와 같은 모양, 사용자 지정 2026-10-16).
 *
 *   순서 카카오 · Google · Apple. 카카오 #FEE500 · Google 흰색 + 1px 테두리 · Apple 검정 + 흰 글자
 *   높이 54 · 반경 14 · 표식 18 을 왼쪽 20 에 고정, 문구 「…로 계속하기」 가운데 15.5/600. 각 사 지침의 색 · 표식을 바꾸지 않습니다
 *
 * - **목록은 서버가 정합니다.** 키가 들어 있는 방법만 그립니다. 다만 개발 빌드는 셋을 다 그리고, 키가 없는 것을 누르면
 *   그렇다고만 말합니다(화면을 짤 때 자리를 보려고)
 * - 로그인 중: 누른 버튼만 문구 자리에 스피너 20, 나머지 버튼 꺼짐(불투명 .5)
 * - **안드로이드에서도 Apple 을 그립니다.** 방식마다 별개 계정이라(D108), Apple 로 가입한 사람이 안드로이드로 바꾸면
 *   들어올 길이 없어집니다(히어로챗 사용자 결정)
 * - 제공자에 갔다가 그냥 돌아오면 버튼을 되살립니다: 웹은 뒤로 가기(bfcache) · 앱은 앱 안 브라우저 닫기 · 앱 복귀
 */
import { useEffect, useRef, useState } from 'react';
import { fetchProviders } from '../api/auth.js';
import { loginPlatform, onLoginBrowserAbandoned, startSocialLogin } from '../lib/social.js';

export const SOCIAL_BUTTONS = [
    {
        name: 'kakao',
        label: '카카오로 계속하기',
        // 카카오 버튼 지침: 바탕 #FEE500, 글자와 말풍선은 검정 85%
        className: 'bg-[#FEE500] text-[rgba(0,0,0,0.85)]',
        logo: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 3C6.48 3 2 6.48 2 10.8c0 2.74 1.67 5.14 4.19 6.6l-1.06 3.93c-.09.35.29.63.59.43L10.3 19.2c.56.07 1.13.1 1.7.1 5.52 0 10-3.48 10-7.8C22 6.48 17.52 3 12 3z" />
            </svg>
        ),
    },
    {
        name: 'google',
        label: 'Google로 계속하기',
        // 구글 지침: 흰 바탕에 1px 테두리. 테두리 색만 우리 토큰
        className: 'bg-[#FFFFFF] text-[#1F1F1F] shadow-[inset_0_0_0_1px_var(--color-hairline)]',
        logo: (
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
            </svg>
        ),
    },
    {
        name: 'apple',
        label: 'Apple로 계속하기',
        // Apple 지침: 검정 바탕에 흰 글자. 이 앱의 Tailwind 는 색을 비우고 토큰만 둬서(styles @theme) 값을 그대로 적습니다
        className: 'bg-[#000000] text-[#FFFFFF]',
        logo: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
        ),
    },
];

function Spinner() {
    return (
        <span
            aria-hidden="true"
            className="block size-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
        />
    );
}

/**
 * @param {{ onNotice?: (message: string) => void, showAll?: boolean }} props
 *   onNotice   화면이 토스트로 띄울 문구(키 없는 방법을 누름)
 *   showAll    키와 상관없이 셋을 다 그림(기본: 개발 빌드만)
 */
export function SocialButtons({ onNotice, showAll = import.meta.env.DEV }) {
    // null 은 아직 모름. 모르는 동안 그리지 않습니다(그렸다가 키가 없어 빠지면 버튼이 사라지는 게 보임)
    const [live, setLive] = useState(null);
    const [leaving, setLeaving] = useState(null);
    const leavingRef = useRef(null);
    leavingRef.current = leaving;

    useEffect(() => {
        const controller = new AbortController();
        fetchProviders({ signal: controller.signal })
            .then((response) => setLive((response.providers ?? []).map((provider) => provider.name)))
            .catch(() => setLive([]));
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const reset = () => setLeaving(null);
        // 웹: 제공자 화면에서 뒤로 가기로 돌아오면 떠나기 직전 화면을 그대로 꺼내 보입니다(bfcache)
        const restored = (event) => {
            if (event.persisted) {
                reset();
            }
        };
        // 앱: 앱 안 브라우저를 닫고 돌아오면 웹뷰는 그대로라 「로그인 중」이 남습니다
        const back = () => {
            if (document.visibilityState === 'visible' && loginPlatform() !== 'web' && leavingRef.current) {
                reset();
            }
        };
        window.addEventListener('pageshow', restored);
        document.addEventListener('visibilitychange', back);
        const stopListening = onLoginBrowserAbandoned(reset);
        return () => {
            window.removeEventListener('pageshow', restored);
            document.removeEventListener('visibilitychange', back);
            stopListening();
        };
    }, []);

    if (live === null && !showAll) {
        return null;
    }
    const shown = SOCIAL_BUTTONS.filter((button) => showAll || live.includes(button.name));
    if (!shown.length) {
        return null;
    }

    const press = (button) => {
        if (live !== null && !live.includes(button.name)) {
            // 개발 빌드에서만 여기 옵니다. 운영은 키가 없는 버튼을 그리지 않습니다
            onNotice?.(`${button.label.replace('로 계속하기', '')} 키가 아직 없어요. server/.env 에 넣으면 연결돼요.`);
            return;
        }
        setLeaving(button.name);
        startSocialLogin(button.name);
    };

    return (
        <div className="flex flex-col gap-[10px] self-stretch">
            {shown.map((button) => {
                const busy = leaving === button.name;
                return (
                    <button
                        key={button.name}
                        type="button"
                        onClick={() => press(button)}
                        disabled={Boolean(leaving)}
                        aria-busy={busy ? 'true' : undefined}
                        aria-label={busy ? `${button.label} · 연결하는 중` : undefined}
                        className={`relative flex min-h-[54px] w-full items-center justify-center rounded-[14px] border-0 px-[52px] text-[15.5px] font-semibold active:opacity-[0.86] ${button.className} ${leaving && !busy ? 'opacity-50' : ''}`}
                    >
                        <span className="absolute inset-y-0 left-5 flex items-center">{button.logo}</span>
                        {busy ? <Spinner /> : button.label}
                    </button>
                );
            })}
        </div>
    );
}
